import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useDialog, usePriceDropPredictionDetails } from "@apac/hooks";
import { alertKeySelector, isWatchingSelector } from "@apac/price-watch";
import {
  Dealness,
  type Prediction,
  type PriceDropMaximumRefund,
} from "@b2bportal/air-shopping-api";
import { getPrediction } from "@b2bportal/core-flights";
import type { DialogProps } from "@b2bportal/core-ui";
import { trackEvent } from "@hopper-b2b/api";
import { useI18nContext } from "@hopper-b2b/i18n";
import { VIEWED_FORECAST } from "@hopper-b2b/types";
import type { PriceForecastItem } from "@hopper-b2b/ui";
import type { WatchTripDialogProps } from "../../WatchTripDialog/WatchTripDialog";

export interface UsePricePredictionResponse {
  watchTripDialogProps: WatchTripDialogProps;
  priceDropDialogProps: DialogProps;
  learnMoreDialogProps: DialogProps;
  context: {
    items: Array<PriceForecastItem>;
    title: string;
    subtitle: string;
    lowestPrice: string;
    prediction: Prediction;

    priceDropAmount?: PriceDropMaximumRefund;
    isPDPEligible: boolean;
    isWatching: boolean;
  };
}

// No titles for each for now, by design
const priceForeCastTitles = [
  { title: "", dealness: Dealness.great },
  { title: "", dealness: Dealness.good },
  { title: "", dealness: Dealness.fair },
  { title: "", dealness: Dealness.wait },
];

export const usePricePrediction = (): UsePricePredictionResponse => {
  const { t, formatFiatCurrency } = useI18nContext();
  const prediction = useSelector(getPrediction);

  const flightKey = useSelector(alertKeySelector);
  const isWatching = useSelector(isWatchingSelector);

  const predictionTitleMap = {
    [Dealness.great]: t("priceForecast.greatDescription"),
    [Dealness.good]: t("priceForecast.goodDescription"),
    [Dealness.fair]: t("priceForecast.fairDescription"),
    [Dealness.wait]: t("priceForecast.waitDescription"),
  };

  const priceDropDialogProps = useDialog();
  const learnMoreDialogProps = useDialog();
  const watchTripDialogProps = {
    ...useDialog(),
    flightKey,
  };

  const { priceDropAmount, isPDPEligible } = usePriceDropPredictionDetails();

  useEffect(() => {
    if (prediction?.recommendationV2) {
      trackEvent({
        eventName: VIEWED_FORECAST,
        properties: {
          recommendation: prediction.recommendationV2,
        },
      });
    }
  }, [prediction?.recommendationV2]);

  return {
    priceDropDialogProps,
    learnMoreDialogProps: {
      ...learnMoreDialogProps,
      open:
        !watchTripDialogProps.open &&
        !priceDropDialogProps.open &&
        learnMoreDialogProps.open,
    },
    watchTripDialogProps: {
      ...watchTripDialogProps,
      onSuccessClose: learnMoreDialogProps.onClose,
    },
    context: {
      prediction,
      items: priceForeCastTitles,
      lowestPrice: formatFiatCurrency(prediction?.lowestPrice?.fiat, {
        maximumFractionDigits: 0,
        minimumFractionDigits: 0,
      }),
      title: predictionTitleMap[prediction?.dealness] || "",
      subtitle: prediction?.predictionCopy?.recommendationTitle.join(" ") || "",
      isWatching,

      priceDropAmount,
      isPDPEligible,
    },
  };
};
